import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Send, User, Phone, Mail, MapPin, Navigation, Calendar, MessageSquare, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';

const Booking = () => {
  // EmailJS Configuration
  const EMAILJS_SERVICE_ID = 'service_ztfrpso';
  const EMAILJS_TEMPLATE_ID = 'template_vamxc22';
  const EMAILJS_PUBLIC_KEY = 'MHODPWt19H-5pdQpl';

  // Initialize EmailJS
  useEffect(() => {
    emailjs.init(EMAILJS_PUBLIC_KEY);
  }, []);

  // Get tomorrow's date in YYYY-MM-DD format
  const getTomorrowDate = () => {
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    return tomorrow.toISOString().split('T')[0];
  };

  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    pickup: '',
    drop: '',
    date: '',
    startDate: '',
    endDate: '',
    tripType: 'one-way',
    message: ''
  });

  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    
    try {
      // Prepare email parameters
      const emailParams = {
        customer_name: formData.name,
        customer_mobile: formData.mobile,
        customer_email: formData.email || 'Not provided',
        pickup_location: formData.pickup,
        drop_location: formData.drop,
        trip_type: formData.tripType,
        travel_date: formData.tripType === 'per-day' 
          ? `${formData.startDate} to ${formData.endDate}` 
          : formData.date,
        additional_message: formData.message || 'No additional message',
        to_email: 'contact@exploredestinationcabs.com'
      };

      // Send email using EmailJS
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        emailParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitted(true);
      
      // Reset form after 5 seconds
      setTimeout(() => {
        setSubmitted(false);
        setFormData({
          name: '',
          mobile: '',
          email: '',
          pickup: '',
          drop: '',
          date: '',
          startDate: '',
          endDate: '',
          tripType: 'one-way',
          message: ''
        });
      }, 5000);
      
    } catch (err) {
      console.error('Booking submission error:', err);
      setError('Failed to submit booking. Please try again or call us at +91-6396867682');
    } finally {
      setLoading(false);
    }
  };

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0 }
  };

  return (
    <div className="min-h-screen pt-24 pb-16" data-testid="booking-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ duration: 0.6 }}
            className="space-y-6"
          >
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 leading-tight tracking-tight">
              Book Your Cab
            </h1>
            <p className="text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Fill out the form below and our team will get back to you shortly with confirmation.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          {!submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden"
            >
              <div className="p-8 md:p-12">
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-semibold text-slate-700 mb-2">
                      Full Name *
                    </label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                      <input
                        type="text"
                        id="name"
                        name="name"
                        data-testid="name-input"
                        value={formData.name}
                        onChange={handleChange}
                        placeholder="Enter your full name"
                        className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                        required
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Mobile */}
                    <div>
                      <label htmlFor="mobile" className="block text-sm font-semibold text-slate-700 mb-2">
                        Mobile Number *
                      </label>
                      <div className="relative">
                        <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="tel"
                          id="mobile"
                          name="mobile"
                          data-testid="mobile-input"
                          value={formData.mobile}
                          onChange={handleChange}
                          placeholder="+91 XXXXX XXXXX"
                          pattern="[0-9]{10}"
                          className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                          required
                        />
                      </div>
                    </div>

                    {/* Email */}
                    <div>
                      <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-2">
                        Email Address
                      </label>
                      <div className="relative">
                        <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="email"
                          id="email"
                          name="email"
                          data-testid="email-input"
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="your@email.com"
                          className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Pickup */}
                    <div>
                      <label htmlFor="pickup" className="block text-sm font-semibold text-slate-700 mb-2">
                        Pickup Location *
                      </label>
                      <div className="relative">
                        <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          id="pickup"
                          name="pickup"
                          data-testid="pickup-input"
                          value={formData.pickup}
                          onChange={handleChange}
                          placeholder="Pickup location"
                          className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                          required
                        />
                      </div>
                    </div>

                    {/* Drop */}
                    <div>
                      <label htmlFor="drop" className="block text-sm font-semibold text-slate-700 mb-2">
                        Drop Location *
                      </label>
                      <div className="relative">
                        <Navigation className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                        <input
                          type="text"
                          id="drop"
                          name="drop"
                          data-testid="drop-input"
                          value={formData.drop}
                          onChange={handleChange}
                          placeholder="Drop location"
                          className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                          required
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    {/* Date - Shows differently based on trip type */}
                    {formData.tripType === 'per-day' ? (
                      <>
                        {/* Start Date */}
                        <div>
                          <label htmlFor="startDate" className="block text-sm font-semibold text-slate-700 mb-2">
                            Start Date *
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                              type="date"
                              id="startDate"
                              name="startDate"
                              data-testid="start-date-input"
                              value={formData.startDate}
                              onChange={handleChange}
                              min={getTomorrowDate()}
                              className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                              required
                            />
                          </div>
                        </div>

                        {/* End Date */}
                        <div>
                          <label htmlFor="endDate" className="block text-sm font-semibold text-slate-700 mb-2">
                            End Date *
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                              type="date"
                              id="endDate"
                              name="endDate"
                              data-testid="end-date-input"
                              value={formData.endDate}
                              onChange={handleChange}
                              min={formData.startDate || getTomorrowDate()}
                              className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                              required
                            />
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Single Date for other trip types */}
                        <div>
                          <label htmlFor="date" className="block text-sm font-semibold text-slate-700 mb-2">
                            Travel Date *
                          </label>
                          <div className="relative">
                            <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                            <input
                              type="date"
                              id="date"
                              name="date"
                              data-testid="date-input"
                              value={formData.date}
                              onChange={handleChange}
                              min={getTomorrowDate()}
                              className="w-full h-14 pl-12 pr-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none"
                              required
                            />
                          </div>
                        </div>

                        {/* Trip Type */}
                        <div>
                          <label htmlFor="tripType" className="block text-sm font-semibold text-slate-700 mb-2">
                            Trip Type *
                          </label>
                          <select
                            id="tripType"
                            name="tripType"
                            data-testid="trip-type-select"
                            value={formData.tripType}
                            onChange={handleChange}
                            className="w-full h-14 px-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none appearance-none cursor-pointer"
                            required
                          >
                            <option value="one-way">One-Way Trip</option>
                            <option value="two-way">Two-Way Trip</option>
                            <option value="per-day">Per Day Package</option>
                            <option value="airport">Airport Transfer</option>
                            <option value="tour">Tour Package</option>
                          </select>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Trip Type - Show outside grid for per-day */}
                  {formData.tripType === 'per-day' && (
                    <div>
                      <label htmlFor="tripType" className="block text-sm font-semibold text-slate-700 mb-2">
                        Trip Type *
                      </label>
                      <select
                        id="tripType"
                        name="tripType"
                        data-testid="trip-type-select"
                        value={formData.tripType}
                        onChange={handleChange}
                        className="w-full h-14 px-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none appearance-none cursor-pointer"
                        required
                      >
                        <option value="one-way">One-Way Trip</option>
                        <option value="two-way">Two-Way Trip</option>
                        <option value="per-day">Per Day Package</option>
                        <option value="airport">Airport Transfer</option>
                        <option value="tour">Tour Package</option>
                      </select>
                      
                      {/* Show number of days if both dates selected */}
                      {formData.startDate && formData.endDate && (
                        <p className="mt-3 text-sm text-blue-600 font-semibold bg-blue-50 px-4 py-2 rounded-lg">
                          Duration: {Math.ceil((new Date(formData.endDate) - new Date(formData.startDate)) / (1000 * 60 * 60 * 24))} days
                        </p>
                      )}
                    </div>
                  )}

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-semibold text-slate-700 mb-2">
                      Additional Message
                    </label>
                    <div className="relative">
                      <MessageSquare className="absolute left-4 top-4 w-5 h-5 text-slate-400" />
                      <textarea
                        id="message"
                        name="message"
                        data-testid="message-textarea"
                        value={formData.message}
                        onChange={handleChange}
                        rows="4"
                        placeholder="Any special requirements or questions..."
                        className="w-full pl-12 pr-4 pt-4 pb-4 bg-slate-50 border-2 border-slate-200 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 rounded-xl transition-all outline-none resize-none"
                      />
                    </div>
                  </div>

                  {/* Error Message */}
                  {error && (
                    <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded-lg">
                      <p className="text-red-700 text-sm">{error}</p>
                    </div>
                  )}

                  {/* Submit Button */}
                  <button
                    type="submit"
                    data-testid="submit-enquiry-btn"
                    disabled={loading}
                    className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 disabled:cursor-not-allowed text-white font-bold py-4 rounded-full transition-all duration-300 shadow-lg hover:shadow-orange-500/30 hover:-translate-y-0.5 uppercase tracking-wide inline-flex items-center justify-center gap-2"
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        <span>Sending...</span>
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        <span>Submit Enquiry</span>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          ) : (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.3 }}
              data-testid="success-message"
              className="bg-white rounded-3xl shadow-2xl border border-slate-200 overflow-hidden p-12 text-center"
            >
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 className="text-3xl font-bold text-slate-900 mb-4">Thank You!</h2>
              <p className="text-lg text-slate-600 mb-6">
                Your booking enquiry has been received successfully.
              </p>
              <p className="text-slate-600 mb-8">
                Our team will contact you shortly at <strong>{formData.mobile}</strong> to confirm your booking.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a
                  href="tel:+916396867682"
                  className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300"
                >
                  Call Us Now
                </a>
                <button
                  onClick={() => setSubmitted(false)}
                  className="border-2 border-blue-600 text-blue-600 hover:bg-blue-50 font-semibold px-8 py-3 rounded-full transition-colors duration-300"
                >
                  Make Another Booking
                </button>
              </div>
            </motion.div>
          )}

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mt-12 text-center"
          >
            <p className="text-slate-600 mb-4">Need immediate assistance?</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="tel:+916396867682"
                data-testid="booking-call-btn"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300"
              >
                📞 +91-6396867682
              </a>
              <a
                href="tel:+918859202773"
                data-testid="booking-call-btn-2"
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-8 py-3 rounded-full transition-colors duration-300"
              >
                📞 +91-8859202773
              </a>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Booking;
